/**
 * Agent 路由服务
 * 负责根据用户输入判断应该使用哪个 agent
 */

import { MultiApp } from '@/config/context';
import i18n from '@/i18n';
import { getUserId } from '@/infrastructure/auth';
import { isVersion } from '@/infrastructure/utils/version-strategy';
import { OptionType } from '@/proto/field';
import { llmOauthService } from '@/service/llm-oauth';

export interface AgentRouteRequest {
  userInput: string;
  availableAgents: string[];
  context?: {
    userId?: string;
    productId?: string;
    sessionId?: string;
  };
}

export interface AgentRouteResponse {
  targetAgent: string;
  confidence?: number;
  reason?: string;
}

/**
 * 调用 LLM 分类 API 进行 agent 路由
 */
export const routeToAgent = async (
  userInput: string,
  availableAgents: OptionType[],
  _context?: AgentRouteRequest['context'],
): Promise<string> => {
  // 如果没有可用 agents，返回默认值
  if (!availableAgents || availableAgents.length === 0) {
    return '';
  }

  // 如果只有一个 agent，直接返回
  if (availableAgents.length === 1) {
    return (availableAgents[0]?.value ?? '') as string;
  }

  // 如果是 WOA 版本，使用原有的 LLM 分类逻辑
  if (isVersion(MultiApp.WOA)) {
    return await routeToAgentByLLM(userInput, availableAgents);
  }

  // 其他版本使用 dispatch 接口
  return await routeToAgentByDispatch(userInput, availableAgents);
};

/**
 * 通过 dispatch 接口获取 agent
 */
const routeToAgentByDispatch = async (
  userInput: string,
  availableAgents: OptionType[],
): Promise<string> => {
  try {
    // 构造输入（与 WOA 版本格式保持一致）
    const requestInput = `
      用户输入： ${userInput},
      可用 agents： ${JSON.stringify(availableAgents)}
    `;

    const agentValue = await llmOauthService.dispatchRouter(requestInput);

    if (agentValue) {
      // 根据 agentValue 查找对应的 agent
      const targetAgent = availableAgents.find(agent => agent.value === agentValue);
      if (targetAgent) {
        return targetAgent.value as string;
      }
    }

    // 如果没有找到对应的 agent，返回第一个
    return availableAgents[0]?.value as string;
  } catch (error) {
    // 错误降级：返回第一个可用 agent
    return availableAgents[0]?.value as string;
  }
};

/**
 * 通过 LLM 分类 API 进行 agent 路由（WOA 版本专用）
 */
const routeToAgentByLLM = async (
  userInput: string,
  availableAgents: OptionType[],
): Promise<string> => {
  // 创建 AbortController 用于超时控制
  const abortController = new AbortController();

  try {
    // LLM 分类 API URL
    const LLM_CLASSIFICATION_API_URL = 'https://knot.woa.com/apigw/api/v1/agents/agui/aa6f0f92d3aa4df5a62c2177cb1379c0';

    // 构造输入
    const reqestInput = `
      用户输入： ${userInput},
      可用 agents： ${JSON.stringify(availableAgents)}
    `;

    // 构建符合 Knot API 格式的请求体
    const requestBody = {
      input: {
        message: reqestInput,
        conversation_id: '', // 每次路由都是新会话
        model: 'deepseek-v3', // 使用 deepseek-v3 模型
        stream: false, // 非流式响应
        enable_web_search: false, // 不需要联网搜索
      },
    };

    const response = await fetch(LLM_CLASSIFICATION_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-knot-token': 'knot_592311e1ea0c4b2fa5e51336279ad79d',
        'X-Username': getUserId(),
        'x-lng': i18n.language,
      },
      body: JSON.stringify(requestBody),
      signal: abortController.signal, // 添加 abort signal
    });

    if (!response.ok) {
      throw new Error(`Agent classification failed: ${response.statusText}`);
    }

    // 解析非流式 JSON 响应
    const result = await response.json();

    // 解析格式: { rawEvent: { content: "agent_name", conversation_id: "...", message_id: "..." } }
    const agentName = result.rawEvent?.content?.trim();

    // 验证返回的 agent 是否在可用列表中
    const agentNames = availableAgents.map(agent => `${agent.value}`);

    if (agentName && agentNames.includes(agentName)) {
      return agentName;
    }

    // 如果返回的 agent 不在列表中，使用第一个
    return availableAgents[0]?.value as string;
  } catch (error) {
    // 特殊处理 AbortError
    if (error instanceof Error && error.name === 'AbortError') {
      return availableAgents[0]?.value as string;
    }

    // 错误降级：返回第一个可用 agent
    return availableAgents[0]?.value as string;
  }
};

/**
 * 本地关键词匹配（作为备选方案或快速路由）
 */
export const quickRouteByKeywords = (
  userInput: string,
  agentOptions: Array<{
    value: string;
    keywords?: string[];
  }>,
): string | null => {
  const input = userInput.toLowerCase();

  for (const agent of agentOptions) {
    if (agent.keywords) {
      for (const keyword of agent.keywords) {
        if (input.includes(keyword.toLowerCase())) {
          return agent.value;
        }
      }
    }
  }

  return null;
};

/**
 * Agent 路由配置
 * 可以根据实际情况扩展
 */
export const AGENT_ROUTE_CONFIG = {
  // 路由超时时间（毫秒）
  timeout: 50000,

  // 是否启用本地关键词匹配作为降级
  enableKeywordFallback: true,

  // 是否在路由时显示 loading 状态
  showLoadingState: true,

  // 默认 agent
  defaultAgent: 'coordinator',
};
